import { useQueries, useQuery } from "@tanstack/react-query";

import {
  ValueChainParams,
  getValueChain,
} from "../../services/productPlaneApi";
import { ValueChain } from "../../types/valueChain";
import { queryKeys } from "./keys";

const STALE_TIME = 60 * 1000;
const GC_TIME = 5 * 60 * 1000;

/**
 * TanStack Query hook for the value chain of one root.
 *
 * The caller builds the request; `null` asks for nothing (no root named yet).
 */
export function useValueChain(params: ValueChainParams | null) {
  return useQuery({
    queryKey: queryKeys.productPlane.valueChain(
      params ?? { root_type: "kind", root_id: "" },
    ),
    queryFn: () => getValueChain(params as ValueChainParams),
    enabled: params !== null,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

// The reads of several requests, in their order: each one's response, or
// undefined while it has not arrived or has failed
const responses = (
  results: { data: ValueChain | undefined }[],
): (ValueChain | undefined)[] => results.map((result) => result.data);

/**
 * TanStack Query hook for several value-chain reads at once: one per opened
 * card, each the same read with the card as root. Returns the responses in
 * the order of the requests; the list keeps its identity while none changes.
 */
export function useValueChains(
  requests: ValueChainParams[],
): (ValueChain | undefined)[] {
  return useQueries({
    queries: requests.map((params) => ({
      queryKey: queryKeys.productPlane.valueChain(params),
      queryFn: () => getValueChain(params),
      staleTime: STALE_TIME,
      gcTime: GC_TIME,
    })),
    combine: responses,
  });
}
